// src/pages/UserAnswersPage.jsx

import React, { useEffect, useState, useRef } from 'react'
import html2canvas from 'html2canvas' // Kolajı tek resme dönüştürmek için
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Radio,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Divider,
  IconButton,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

// ReactQuill Kaldırıldı!
// import ReactQuill from 'react-quill'
// import 'react-quill/dist/quill.snow.css'

// Medya yükleme aracı
import { uploadFile } from '../utils/uploadFile'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

import CollageCanvas from '../components/CollageCanvas'

/**
 * ======================
 * AI Moderator Chat UI
 * ======================
 *
 * Burada Quill kullanmadan, sadece düz TextField ile kullanıcı mesajı alıyoruz
 * Bu kısım değişmedi.
 */
function AiModeratorChat({ question, conversation, onSendMessage, isSubmitting, isAiLoading }) {
  const [userMessage, setUserMessage] = useState('')
  const aiCount = question.aiCount || 0
  const aiCountPlusOne = aiCount + 1
  const { t } = useTranslation()

  // Kaç tane user-rolü mesajı girildiğini sayalım
  const userMessageCount = conversation.filter((m) => m.role === 'user').length

  const handleSend = () => {
    if (!userMessage.trim()) return
    onSendMessage(userMessage.trim())
    setUserMessage('')
  }

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: 2,
          p: 2,
          mb: 2,
          maxHeight: 300,
          overflowY: 'auto'
        }}
      >
        {conversation.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            {t('userAnswersPage.chatScreen')}
          </Typography>
        )}
        {conversation
          .filter((msg) => msg.role !== 'question')
          .map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 1,
                textAlign: msg.role === 'assistant' ? 'left' : 'right'
              }}
            >
              <Paper
                sx={{
                  display: 'inline-block',
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: msg.role === 'assistant' ? '#f1f1f1' : '#d1e7ff'
                }}
              >
                <Typography variant="body2">{msg.content}</Typography>
              </Paper>
            </Box>
          ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          variant="outlined"
          fullWidth
          size="small"
          value={userMessage}
          onChange={(e) => setUserMessage(e.target.value)}
          placeholder={t('userAnswersPage.aiMessagePlaceholder')}
          disabled={isSubmitting || (aiCountPlusOne > 0 && userMessageCount >= aiCountPlusOne)}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isSubmitting || (aiCountPlusOne > 0 && userMessageCount >= aiCountPlusOne)}
        >
          {t('userAnswersPage.aiSendButton')}
        </Button>
      </Box>

      {isAiLoading && (
        <Box mt={1} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2">{t('userAnswersPage.aiWaiting')}</Typography>
        </Box>
      )}

      {aiCountPlusOne > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          {t('userAnswersPage.aiLimitedPrompt', {
            count: Math.max(aiCountPlusOne - userMessageCount, 0)
          })}
        </Typography>
      )}
    </Box>
  )
}

export default function UserAnswersPage() {
  const { currentUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { channelId, questionFormId } = location.state || {}

  const [questionFormStatus, setQuestionFormStatus] = useState(null)
  const [questions, setQuestions] = useState([])
  const [loadingQuestions, setLoadingQuestions] = useState(false)
  const [hasAnswered, setHasAnswered] = useState(false)
  const [answersState, setAnswersState] = useState({})
  const [activeStep, setActiveStep] = useState(0)
  const [responseDocId, setResponseDocId] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [aiLoading, setAiLoading] = useState(false)

  // Medya yükleme anında sayfayı kitlemek için
  const [mediaUploading, setMediaUploading] = useState(false)

  const [isResponsesFetched, setIsResponsesFetched] = useState(false)
 
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Kolaj (photoCollage) için state'ler
  const [collageDialogOpen, setCollageDialogOpen] = useState(false)
  const [collageQuestionId, setCollageQuestionId] = useState(null)
  const [collageItems, setCollageItems] = useState([])
  const collageRef = useRef(null)

  // Video kaydı için state'ler
  const [videoDialogOpen, setVideoDialogOpen] = useState(false)
  const [videoQuestionId, setVideoQuestionId] = useState(null)
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [cameraStream, setCameraStream] = useState(null)
  const [recordedChunks, setRecordedChunks] = useState([])
  const [isRecording, setIsRecording] = useState(false)
  const [disableStop, setDisableStop] = useState(false)
  const videoRef = useRef(null)

  const [uploadProgress, setUploadProgress] = useState(0); 

  // ================================
  // Check channelId & questionFormId
  // ================================
  useEffect(() => {
    if (!channelId || !questionFormId) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.errorNoChannelOrForm'),
        severity: 'error'
      })
    }
  }, [channelId, questionFormId, t])

  // ================================
  // Fetch form & questions
  // ================================
  useEffect(() => {
    if (!currentUser || !channelId || !questionFormId) return

    async function fetchData() {
      try {
        setLoadingQuestions(true)

        // Form dokümanı
        const formDoc = await getDoc(doc(db, 'questionForms', questionFormId))
        if (!formDoc.exists()) {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.loadingDataError'),
            severity: 'error'
          })
          setLoadingQuestions(false)
          return
        }
        const formData = formDoc.data()
        setQuestionFormStatus(formData.status)

        // Yayınlanmamış form
        if (formData.status !== 'published') {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.errorNotPublished'),
            severity: 'error'
          })
          setLoadingQuestions(false)
          return
        }

        // Daha önce yanıt vermiş mi
        const respQuery = query(
          collection(db, 'responses'),
          where('userId', '==', currentUser.uid),
          where('channelId', '==', channelId),
          where('questionFormId', '==', questionFormId)
        )
        const respSnap = await getDocs(respQuery)

        let partialResponseData = null

        if (!respSnap.empty) {
          const existingDoc = respSnap.docs[0]
          const existingData = existingDoc.data()
          setResponseDocId(existingDoc.id)

          if (existingData.status === 'completed') {
            setHasAnswered(true)
            setLoadingQuestions(false)
            setIsResponsesFetched(true)
            return
          } else {
            partialResponseData = existingData
          }
        }

        // Soru dokümanları
        const qQuery = query(
          collection(db, 'questions'),
          where('channelId', '==', channelId),
          where('questionFormId', '==', questionFormId),
          orderBy('sortOrder', 'asc')
        )
        const qSnap = await getDocs(qQuery)
        const qs = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }))

        setQuestions(qs)

        // answersState başlat
        const initAns = {}
        qs.forEach((q) => {
          switch (q.questionType) {
            case 'openEnded':
              initAns[q.id] = { text: '', media: [] }
              break
            case 'singleChoice':
              if (q.allowOther) {
                initAns[q.id] = {
                  choice: '',
                  otherChecked: false,
                  otherText: ''
                }
              } else {
                initAns[q.id] = ''
              }
              break
            case 'multipleChoice':
              if (q.allowOther) {
                initAns[q.id] = {
                  choices: [],
                  otherChecked: false,
                  otherText: ''
                }
              } else {
                initAns[q.id] = []
              }
              break
            case 'ratingQuestion':
              initAns[q.id] = 0
              break
            case 'videoQuestion':
              initAns[q.id] = { videoUrl: '' }
              break
            case 'photoCollage':
              initAns[q.id] = {
                collageSingleUrl: ''
              }
              break
            case 'aiModerator':
              initAns[q.id] = {
                conversation: []
              }
              break
            case 'infoQuestion':
              initAns[q.id] = null
              break
            case 'cardSorting':
              initAns[q.id] = { sorted: {} };
              break;
            default:
              initAns[q.id] = null
          }
        })

        // inProgress yanıtları varsa merge et
        if (partialResponseData?.answers) {
          Object.keys(partialResponseData.answers).forEach((qid) => {
            initAns[qid] = partialResponseData.answers[qid]
          })
          if (partialResponseData.progress) {
            setActiveStep(partialResponseData.progress)
          }
        }

        setAnswersState(initAns)
        setLoadingQuestions(false)
        setIsResponsesFetched(true)
      } catch (err) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.loadingDataError'),
          severity: 'error'
        })
        setLoadingQuestions(false)
        setIsResponsesFetched(true)
      }
    }

    fetchData()
  }, [currentUser, channelId, questionFormId, t])

  // ================================
  // Various answer update helpers
  // ================================
  const handleOpenEndedTextChange = (questionId, content) => {
    setAnswersState((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        text: content
      }
    }))
  }

  const handleSingleChoiceChange = (questionId, choice) => {
    const qVal = answersState[questionId]
    if (typeof qVal === 'string') {
      setAnswersState((prev) => ({
        ...prev,
        [questionId]: choice
      }))
    } else {
      setAnswersState((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          choice,
          otherChecked: false,
          otherText: ''
        }
      }))
    }
  }

  const handleMultipleChoiceChange = (questionId, choice) => {
    const qVal = answersState[questionId]
    if (Array.isArray(qVal)) {
      // Basit array
      if (qVal.includes(choice)) {
        setAnswersState((prev) => ({
          ...prev,
          [questionId]: qVal.filter((c) => c !== choice)
        }))
      } else {
        setAnswersState((prev) => ({
          ...prev,
          [questionId]: [...prev[questionId], choice]
        }))
      }
    } else {
      // allowOther
      const choicesArr = qVal.choices
      if (choicesArr.includes(choice)) {
        setAnswersState((prev) => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            choices: choicesArr.filter((c) => c !== choice)
          }
        }))
      } else {
        setAnswersState((prev) => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            choices: [...choicesArr, choice]
          }
        }))
      }
    }
  }

  const toggleOtherChecked = (questionId) => {
    setAnswersState((prev) => {
      const val = prev[questionId]
      return {
        ...prev,
        [questionId]: {
          ...val,
          otherChecked: !val.otherChecked
        }
      }
    })
  }

  const handleOtherTextChange = (questionId, text) => {
    setAnswersState((prev) => {
      const val = prev[questionId]
      return {
        ...prev,
        [questionId]: {
          ...val,
          otherText: text
        }
      }
    })
  }

  const handleRatingChange = (questionId, ratingValue) => {
    setAnswersState((prev) => ({
      ...prev,
      [questionId]: ratingValue
    }))
  }

  // ================================
  // OpenEnded: add/remove media
  // ================================
  const handleAddMedia = async (questionId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*,audio/*';
    input.multiple = true;
    // Hide the input if you don't want it visible
    input.style.display = 'none';
    
    // Append to DOM so iOS 18 recognizes it as part of the document
    document.body.appendChild(input);
  
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;
  
      setMediaUploading(true);
      setUploadProgress(0);
  
      try {
        const uploadedMedia = [];
        const totalFiles = files.length;
        let completedFiles = 0;
  
        for (let file of files) {
          const remoteUrl = await uploadFile(file, (progress) => {
            const partialProgress = (completedFiles + progress / 100) / totalFiles * 100;
            setUploadProgress(Math.round(partialProgress));
          });
  
          completedFiles++;
          setUploadProgress(Math.round((completedFiles / totalFiles) * 100));
  
          const fileType = file.type.startsWith('image/')
            ? 'image'
            : file.type.startsWith('video/')
            ? 'video'
            : file.type.startsWith('audio/')
            ? 'audio'
            : 'file';
  
          uploadedMedia.push({ remoteUrl, fileType });
        }
  
        setAnswersState((prev) => {
          const prevQ = prev[questionId];
          return {
            ...prev,
            [questionId]: {
              ...prevQ,
              media: [...(prevQ.media || []), ...uploadedMedia],
            },
          };
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || t('userAnswersPage.videoUploadError'),
          severity: 'error',
        });
      } finally {
        setMediaUploading(false);
        setUploadProgress(0);
        // Remove the input from the DOM once done
        document.body.removeChild(input);
      }
    };
  
    input.click();
  };

  const handleRemoveMedia = (questionId, mediaIndex) => {
    setAnswersState((prev) => {
      const mediaArr = [...(prev[questionId].media || [])]
      mediaArr.splice(mediaIndex, 1)
      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
          media: mediaArr
        }
      }
    })
  }

  // ================================
  // VIDEO QUESTION Dialog
  // ================================
  const handleVideoQuestion = (questionId) => {
    setVideoQuestionId(questionId)
    setVideoDialogOpen(true)
  }

  const handleToggleRecording = async () => {
    if (!isRecording) {
      // START
      try {
        const constraints = {
          audio: true,
          video: true
        }

        if (!('MediaRecorder' in window)) {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.noVideoSupport'),
            severity: 'warning'
          })
          return
        }

        let mimeType
        if (window.MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
          mimeType = 'video/webm;codecs=vp9'
        } else if (window.MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
          mimeType = 'video/webm;codecs=vp8'
        } else if (window.MediaRecorder.isTypeSupported('video/mp4;codecs=h264')) {
          mimeType = 'video/mp4;codecs=h264'
        } else if (
          window.MediaRecorder.isTypeSupported('video/mp4;codecs="avc1.424028, mp4a.40.2"')
        ) {
          mimeType = 'video/mp4;codecs="avc1.424028, mp4a.40.2"'
        } else {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.noCompatibleVideoFormat'),
            severity: 'error'
          })
          return
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        setCameraStream(stream)

        setRecordedChunks([])
        const recorder = new MediaRecorder(stream, { mimeType })
        setMediaRecorder(recorder)

        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            setRecordedChunks((prev) => [...prev, event.data])
          }
        }

        recorder.onstart = () => {}
        recorder.onstop = () => {}
        recorder.onerror = () => {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.videoRecordError'),
            severity: 'error'
          })
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.muted = true
          videoRef.current.setAttribute('playsinline', 'true')
          videoRef.current.setAttribute('webkit-playsinline', 'true')
          try {
            await videoRef.current.play()
          } catch {}
        }

        recorder.start(1000)
        setIsRecording(true)

        setDisableStop(true)
        setTimeout(() => {
          setDisableStop(false)
        }, 2000)
      } catch (error) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.cameraError'),
          severity: 'error'
        })
      }
    } else {
      // STOP
      if (disableStop) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.shortRecordingWarn'),
          severity: 'warning'
        })
        return
      }
      try {
        if (mediaRecorder) {
          mediaRecorder.stop()
        }
        if (cameraStream) {
          cameraStream.getTracks().forEach((track) => track.stop())
        }
        setIsRecording(false)

        setTimeout(async () => {
          if (recordedChunks.length === 0) {
            setSnackbar({
              open: true,
              message: t('userAnswersPage.videoMandatoryError'),
              severity: 'error'
            })
            return
          }

          setMediaUploading(true)
          try {
            const mimeTypeUsed = mediaRecorder.mimeType
            const videoBlob = new Blob(recordedChunks, { type: mimeTypeUsed })

            if (!videoBlob || videoBlob.size === 0) {
              setSnackbar({
                open: true,
                message: t('userAnswersPage.videoMandatoryError'),
                severity: 'error'
              })
              setMediaUploading(false)
              return
            }

            const extension = mimeTypeUsed.includes('mp4') ? 'mp4' : 'webm'
            const fileType = mimeTypeUsed.includes('mp4') ? 'video/mp4' : 'video/webm'
            const file = new File([videoBlob], `recorded_video.${extension}`, { type: fileType })

            setRecordedChunks([])

            const remoteUrl = await uploadFile(file)
            setVideoPreviewUrl(remoteUrl)
            setSnackbar({
              open: true,
              message: t('userAnswersPage.videoRecordedMsg'),
              severity: 'success'
            })
          } catch (err) {
            setSnackbar({
              open: true,
              message: t('userAnswersPage.videoUploadError'),
              severity: 'error'
            })
          } finally {
            setMediaUploading(false)
          }
        }, 500)
      } catch (err) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.videoUploadError'),
          severity: 'error'
        })
      }
    }
  }

  const handleConfirmVideo = () => {
    setAnswersState((prev) => ({
      ...prev,
      [videoQuestionId]: {
        videoUrl: videoPreviewUrl
      }
    }))
    handleCancelVideo()
  }

  const handleCancelVideo = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop())
    }
    setCameraStream(null)
    setMediaRecorder(null)
    setRecordedChunks([])
    setVideoPreviewUrl(null)
    setVideoQuestionId(null)
    setIsRecording(false)
    setDisableStop(false)
    setVideoDialogOpen(false)
  }

  // ================================
  // PHOTO COLLAGE Handlers
  // ================================
  const openCollageEditor = (questionId) => {
    setCollageQuestionId(questionId)
    setCollageItems([])
    setCollageDialogOpen(true)
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }

  async function handleAddCollageImage() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = true

    input.onchange = async () => {
      const files = Array.from(input.files || [])
      if (!files.length) return

      try {
        const newCollageItems = []
        for (let file of files) {
          const base64Data = await fileToBase64(file)
          newCollageItems.push({
            imageUrl: base64Data,
            x: 0,
            y: 0,
            width: 100,
            height: 100,
            rotation: 0
          })
        }
        setCollageItems((prev) => [...prev, ...newCollageItems])
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || t('userAnswersPage.loadingDataError'),
          severity: 'error'
        })
      }
    }

    input.click()
  }

  const handleRemoveCollageItem = (index) => {
    setCollageItems((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSaveCollage = async () => {
    try {
      if (!collageRef.current) {
        throw new Error(t('userAnswersPage.collageError'))
      }

      setMediaUploading(true)
      // Kolajı tek resme dönüştür
      const canvas = await html2canvas(collageRef.current, {
        useCORS: true,
        scale: 2
      })
      const dataURL = canvas.toDataURL('image/png')
      const file = dataURLtoFile(dataURL, 'collage.png')

      // Tek resim olarak Firebase'e yükle
      const remoteUrl = await uploadFile(file)

      setAnswersState((prev) => ({
        ...prev,
        [collageQuestionId]: {
          collageSingleUrl: remoteUrl
        }
      }))

      setCollageDialogOpen(false)
      setCollageItems([])
      setCollageQuestionId(null)

      setSnackbar({
        open: true,
        message: t('userAnswersPage.collageSaveSuccess'),
        severity: 'success'
      })
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.loadingDataError') + (err.message || err),
        severity: 'error'
      })
    } finally {
      setMediaUploading(false)
    }
  }

  const handleCancelCollage = () => {
    setCollageDialogOpen(false)
    setCollageItems([])
    setCollageQuestionId(null)
  }

  function dataURLtoFile(dataURL, filename) {
    const arr = dataURL.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
  }

  // ================================
  // AI MODERATOR: mesaj gönderme
  // ================================
  const handleAiModeratorSend = async (questionId, userText) => {
    try {
      // Önce local conversation'a user mesajını ekle
      const oldVal = answersState[questionId] || { conversation: [] }
      let newConversation = [...oldVal.conversation]

      if (newConversation.length === 0) {
        const qData = questions.find((q) => q.id === questionId)
        if (qData) {
          // Soru metninden HTML etiketlerini temizleyerek sadece düz metin alalım
          const questionTextOnly = qData.text.replace(/<[^>]*>/g, '')
          newConversation.push({ role: 'question', content: questionTextOnly })
        }
      }

      newConversation.push({ role: 'user', content: userText })

      setAnswersState((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          conversation: newConversation
        }
      }))

      // Mesaj sayısını kontrol et
      const userMessageCount = newConversation.filter((m) => m.role === 'user').length
      const aiCount = questions.find((q) => q.id === questionId)?.aiCount || 0
      const aiCountPlusOne = aiCount + 1

      // Son kullanıcı mesaj hakkı dolmuşsa API çağrısı yapma
      if (userMessageCount === aiCountPlusOne) {
        return
      }

      // Normalde API'ye gidecek => spinner aç
      setAiLoading(true)

      const qData = questions.find((q) => q.id === questionId)
      if (!qData) return

      const aiPrompt = qData.aiPrompt || ''
      const bodyData = {
        prompt: aiPrompt,
        conversation: newConversation
      }

      const response = await fetch(
        'https://europe-west3-evosurvey.cloudfunctions.net/qip-evoo-comminity',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bodyData)
        }
      )
      const result = await response.json()

      // Gelen cevabı asistan cevabı olarak ekle
      const assistantMsg = {
        role: 'assistant',
        content: result?.text || t('userAnswersPage.noAnswerformAI')
      }

      const finalConversation = [...newConversation, assistantMsg]

      setAnswersState((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          conversation: finalConversation
        }
      }))

      await savePartialAnswers(activeStep)
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.loadingDataError') + error.message,
        severity: 'error'
      })
    } finally {
      setAiLoading(false)
    }
  }

  // ================================
  // Partial (in-progress) save
  // ================================
  const savePartialAnswers = async (nextStepIndex) => {
    try {
      if (!questions.length) return
      if (!isResponsesFetched) return
      const progress = nextStepIndex
      if (!responseDocId) {
        const ref = await addDoc(collection(db, 'responses'), {
          userId: currentUser.uid,
          channelId,
          questionFormId,
          createdAt: serverTimestamp(),
          status: 'inProgress',
          progress,
          totalQuestions: questions.length,
          answers: answersState
        })
        setResponseDocId(ref.id)
      } else {
        await updateDoc(doc(db, 'responses', responseDocId), {
          status: 'inProgress',
          progress,
          totalQuestions: questions.length,
          answers: answersState
        })
      }
    } catch {}
  }

  // ================================
  // Required checks
  // ================================
  const checkIfRequiredQuestionAnswered = (q) => {
    if (!q.isRequired) return true
    const val = answersState[q.id]
    switch (q.questionType) {
      case 'openEnded': {
        const plainText = (val?.text || '').trim()
        return plainText.length > 0
      }
      case 'singleChoice': {
        if (q.allowOther && typeof val === 'object') {
          if (val.otherChecked) {
            return (val.otherText || '').trim().length > 0
          }
          return (val?.choice || '').length > 0
        }
        return !!val
      }
      case 'multipleChoice': {
        if (q.allowOther && typeof val === 'object') {
          const selectedCount = val.choices?.length || 0
          if (val.otherChecked) {
            return selectedCount > 0 || (val.otherText || '').trim().length > 0
          }
          return selectedCount > 0
        }
        return val && val.length > 0
      }
      case 'ratingQuestion':
        return val && val > 0
      case 'videoQuestion':
        return val?.videoUrl
      case 'photoCollage':
        return !!val?.collageSingleUrl
      case 'aiModerator':
        if (!val || !Array.isArray(val.conversation)) return false
        return val.conversation.some((m) => m.role === 'user')
      case 'infoQuestion':
        return true
      case 'cardSorting': {
        const assignments = val?.sorted || {};
        // Exclude 'unsorted' group from assigned cards count
        const assignedCards = Object.entries(assignments)
          .filter(([groupName]) => groupName !== 'unsorted')
          .flatMap(([_, cards]) => cards);
        return q.isRequired ? assignedCards.length === q.cards.length : true;
      }
      default:
        return true
    }
  }

  // ================================
  // Stepper next/back
  // ================================
  const handleNext = async () => {
    const currentQ = questions[activeStep]
    if (!checkIfRequiredQuestionAnswered(currentQ)) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.requiredQuestionError'),
        severity: 'error'
      })
      return
    }
    if (currentQ.questionType === 'openEnded' && currentQ.requireMedia) {
      const val = answersState[currentQ.id]
      if (!val?.media || val.media.length === 0) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.requiredMediaError'),
          severity: 'error'
        })
        return
      }
    }

    await savePartialAnswers(activeStep + 1)
    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }
  const isLastStep = activeStep === questions.length - 1

  // ================================
  // Final submit
  // ================================
  const handleSubmitAnswers = async () => {
    if (!channelId || !questionFormId) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.noFormOrChannel'),
        severity: 'warning'
      })
      return
    }

    setIsSubmitting(true)

    try {
      for (const q of questions) {
        if (!checkIfRequiredQuestionAnswered(q)) {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.requiredQuestionMissing', {
              questionText: q.text.replace(/<[^>]+>/g, '')
            }),
            severity: 'error'
          })
          setIsSubmitting(false)
          return
        }
        if (q.questionType === 'openEnded' && q.requireMedia) {
          const val = answersState[q.id]
          if (!val?.media || val.media.length === 0) {
            setSnackbar({
              open: true,
              message: t('userAnswersPage.requiredMediaMissing', {
                questionText: q.text.replace(/<[^>]+>/g, '')
              }),
              severity: 'error'
            })
            setIsSubmitting(false)
            return
          }
        }
      }

      const finalAnswersArray = []
      questions.forEach((q) => {
        const val = answersState[q.id]
        let userAnswer = null

        switch (q.questionType) {
          case 'openEnded':
            userAnswer = {
              text: val?.text || '',
              media: val?.media || []
            }
            break
          case 'singleChoice':
            if (q.allowOther && typeof val === 'object') {
              if (val.otherChecked) {
                userAnswer = `Diğer: ${val.otherText || ''}`
              } else {
                userAnswer = val.choice
              }
            } else {
              userAnswer = val
            }
            break
          case 'multipleChoice':
            if (q.allowOther && typeof val === 'object') {
              const selectedArr = val.choices || []
              if (val.otherChecked) {
                userAnswer = [...selectedArr, `Diğer: ${val.otherText || ''}`]
              } else {
                userAnswer = selectedArr
              }
            } else {
              userAnswer = val
            }
            break
          case 'ratingQuestion':
            userAnswer = val
            break
          case 'videoQuestion':
            userAnswer = val?.videoUrl
            break
          case 'photoCollage':
            userAnswer = { collageSingleUrl: val?.collageSingleUrl || '' }
            break
          case 'aiModerator':
            userAnswer = val?.conversation || []
            break
          case 'infoQuestion':
            userAnswer = null
            break
          case 'cardSorting': {
            const sortedData = val?.sorted || {};
            const cardsWithText = {};
          
            // Her grup için kart indekslerini metin ve resme dönüştür
            Object.entries(sortedData).forEach(([groupName, cardIndices]) => {
              cardsWithText[groupName] = cardIndices.map(index => ({
                text: q.cards[index]?.text || '', // Kart metni
                imageUrl: q.cards[index]?.imageUrl || '' // Kart resmi URL
              }));
            });
          
            userAnswer = cardsWithText;
            break;
          }
          default:
            userAnswer = val
            break
        }

        finalAnswersArray.push({
          questionId: q.id,
          questionText: q.text,
          answer: userAnswer
        })
      })

      if (!responseDocId) {
        await addDoc(collection(db, 'responses'), {
          userId: currentUser.uid,
          channelId,
          questionFormId,
          createdAt: serverTimestamp(),
          status: 'completed',
          completedAt: serverTimestamp(),
          progress: questions.length,
          totalQuestions: questions.length,
          answers: answersState,
          finalAnswers: finalAnswersArray
        })
      } else {
        await updateDoc(doc(db, 'responses', responseDocId), {
          status: 'completed',
          completedAt: serverTimestamp(),
          progress: questions.length,
          answers: answersState,
          finalAnswers: finalAnswersArray
        })
      }

      setSnackbar({
        open: true,
        message: t('userAnswersPage.answersSavedSuccess'),
        severity: 'success'
      })
      setHasAnswered(true)
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.answersSavedError'),
        severity: 'error'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  function CardSortingComponent({ question, answersState, setAnswersState }) {
    // Bu bileşende local state tutmak yerine,
    // "unsorted" ve grup atamalarını parent’taki answersState içinde depolayacağız.
    // Aşağıdaki kodda “unSorted” benzeri bir array'i "sorted" objesine ekliyoruz.

    console.log('question',question)

    // 1) "sorted" objesini parent’tan al
    const sortedData = answersState[question.id]?.sorted || {}

    // 2) "unsorted" dizisi var mı yok mu kontrol edelim.
    //    Eğer yoksa, biz oluşturacağız (ilk defa).
    //    Tüm kart index'lerini “unsorted”e atıp, parent state’e kaydetmeliyiz.
    React.useEffect(() => {
      if (!sortedData.unsorted) {
        // Make sure question.cards is valid
        const cardsArray = Array.isArray(question.cards) ? question.cards : []
        // Initialize all cards as “unsorted”
        const allCardIndices = cardsArray.map((_, idx) => idx)
        const newSorted = { unsorted: allCardIndices }
      
        // If question.groups also exists, set them up as empty arrays
        if (Array.isArray(question.groups)) {
          question.groups.forEach((grp) => {
            newSorted[grp] = []
          })
        }
      
        setAnswersState((prev) => ({
          ...prev,
          [question.id]: {
            ...prev[question.id],
            sorted: newSorted
          }
        }))
      }
      // eslint-disable-next-line
    }, [])

    // 3) onDragEnd
    const onDragEnd = (result) => {
      const { source, destination } = result
      if (!destination) return

      const sourceId = source.droppableId
      const destId = destination.droppableId

      // Aynı yer + aynı index'e bıraktıysa
      if (sourceId === destId && source.index === destination.index) {
        return
      }

      // sortedData'yi kopyala (immutable)
      const newSorted = structuredClone(sortedData)

      // Kaynak listeden kart index'ini çıkart
      const movedCard = newSorted[sourceId].splice(source.index, 1)[0]

      // Hedef listeye ekle
      newSorted[destId].splice(destination.index, 0, movedCard)

      // Parent state güncelle
      setAnswersState((prev) => ({
        ...prev,
        [question.id]: {
          ...prev[question.id],
          sorted: newSorted
        }
      }))
    }

    return (
      <Container maxWidth="lg">
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('userAnswersPage.cardSortingIntro')}
        </Typography>

        <DragDropContext onDragEnd={onDragEnd}>
          {/* Unsorted Alanı */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('userAnswersPage.cardSortingUnsorted')}
            </Typography>
            <Droppable droppableId="unsorted" direction="horizontal">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    minHeight: 120,
                    border: '1px dashed #ccc',
                    p: 2
                  }}
                >
                  {(sortedData.unsorted || []).map((cardIndex, index) => (
                    <Draggable
                      key={`unsorted-${cardIndex}`}
                      draggableId={`unsorted-${cardIndex}`}
                      index={index}
                    >
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <CardItem card={question.cards[cardIndex]} />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Box>

          {/* Her grup için ayrı Droppable */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            {question.groups?.map((grpName) => (
              <Droppable key={grpName} droppableId={grpName}>
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      flex: 1,
                      minHeight: 150,
                      border: '1px solid #ccc',
                      p: 2
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {grpName}
                    </Typography>
                    {(sortedData[grpName] || []).map((cardIndex, index) => (
                      <Draggable
                        key={`${grpName}-${cardIndex}`}
                        draggableId={`${grpName}-${cardIndex}`}
                        index={index}
                      >
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{ mb: 2 }}
                          >
                            <CardItem card={question.cards[cardIndex]} />
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            ))}
          </Box>
        </DragDropContext>
      </Container>
    )
  }
  
   // Görsel kart tasarımı
   function CardItem({ card }) {
    return (
      <Paper
        sx={{
          width: 150,
          height: 100,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #ccc'
        }}
      >
        {card.imageUrl && (
          <img
            src={card.imageUrl}
            alt="card"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        )}
        {!card.imageUrl && (
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            {card.text}
          </Typography>
        )}
      </Paper>
    )
  }

  return (
    <>
      <Backdrop open={isSubmitting || mediaUploading} sx={{ color: '#fff', zIndex: 9999 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress color="inherit" />
          {/* Yüzde göstereceksek: */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography variant="body2" sx={{ mt: 2 }}>
                %{uploadProgress} 
              </Typography>
              <Box width={200}>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            </Box>
          )}
        </Box>
      </Backdrop>

      <Container
        maxWidth="xl"
        sx={{
          mt: 4,
          mb: 4,
          pointerEvents: isSubmitting || mediaUploading ? 'none' : 'auto',
          overflow: 'auto'
        }}
      >
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          {t('userAnswersPage.formSubmitTitle')}
        </Typography>

        {/* Kanal veya Form geçersiz */}
        {!channelId || !questionFormId ? (
          <Paper sx={{ p: 2, mb: 4 }}>
            <Typography color="error">
              {t('userAnswersPage.noFormOrChannel')}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/todolist')} sx={{ mt: 2 }}>
              {t('userAnswersPage.formListReturn')}
            </Button>
          </Paper>
        ) : questionFormStatus !== 'published' ? (
          <Paper sx={{ p: 2, mb: 4 }}>
            <Typography color="error" variant="body1" gutterBottom>
              {t('userAnswersPage.formNotPublished')}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/todolist')} sx={{ mt: 2 }}>
              {t('userAnswersPage.formListReturn')}
            </Button>
          </Paper>
        ) : hasAnswered ? (
          <Paper sx={{ p: 2, mb: 4 }}>
            <Typography color="primary" variant="h6" gutterBottom>
              {t('userAnswersPage.alreadyAnswered')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('userAnswersPage.alreadyAnsweredHint')}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/todolist')} sx={{ mt: 2 }}>
              {t('userAnswersPage.formListReturn')}
            </Button>
          </Paper>
        ) : loadingQuestions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : questions.length === 0 ? (
          <Paper sx={{ p: 2, mb: 4 }}>
            <Typography color="textSecondary">{t('userAnswersPage.noQuestions')}</Typography>
            <Divider sx={{ my: 2 }} />
            <Button variant="contained" onClick={() => navigate('/todolist')}>
              {t('userAnswersPage.formListReturn')}
            </Button>
          </Paper>
        ) : (
          // Form Stepper
          <Paper sx={{ p: 2, mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {questions.map((_, idx) => (
                <Step key={idx}>
                  <StepLabel>
                    {t('userAnswersPage.stepperQuestionLabel', { index: idx + 1 })}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Soru Alanı */}
            {questions[activeStep] && (
              <Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {t('userAnswersPage.questionTitle')}
                  </Typography>
                  <Typography
                    variant="body1"
                    dangerouslySetInnerHTML={{ __html: questions[activeStep].text }}
                    sx={{
                      mb: 1,
                      // Tüm img etiketlerine genişliği kapsayıcıya göre ayarla
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                        display: 'block', // (isteğe bağlı) satır içi boşlukları engellemek için block
                      },
                      // Quill vb. içeriğindeki hizalamalar için de kural ekleyebilirsiniz
                      '& .ql-align-center': { textAlign: 'center' },
                      '& .ql-align-right': { textAlign: 'right' },
                      '& .ql-align-justify': { textAlign: 'justify' },
                    }}
                  />
                  {questions[activeStep].isRequired && (
                    <Typography variant="body2" color="error">
                      {t('userAnswersPage.questionRequiredMark')}
                    </Typography>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {t('userAnswersPage.answerTitle')}
                  </Typography>
                  {(() => {
                    const q = questions[activeStep]
                    const qType = q.questionType || 'openEnded'
                    const val = answersState[q.id]

                    switch (qType) {
                      /**
                       * ====> Sadece Düz Metin Girişine Geçildi <====
                       * ReactQuill yerine TextField (multiline) kullanıyoruz.
                       */
                      case 'openEnded':
                        return (
                          <Box>
                            <TextField
                              multiline
                              minRows={4}
                              fullWidth
                              value={val?.text || ''}
                              onChange={(e) => handleOpenEndedTextChange(q.id, e.target.value)}
                              disabled={isSubmitting}
                            />
                            <Box mt={2}>
                              <Button
                                variant="outlined"
                                onClick={() => handleAddMedia(q.id)}
                                disabled={isSubmitting}
                              >
                                {t('userAnswersPage.openEndedMediaAdd')}
                              </Button>
                              <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                                {val?.media?.map((m, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      position: 'relative',
                                      border: '1px solid #ccc',
                                      borderRadius: 2,
                                      p: 1
                                    }}
                                  >
                                    {m.fileType === 'image' && (
                                      <img
                                        src={m.remoteUrl}
                                        alt={`media-${idx}`}
                                        style={{ width: '100%', height: 'auto' }}
                                      />
                                    )}
                                    {m.fileType === 'video' && (
                                      <video src={m.remoteUrl} controls style={{ maxWidth: 200 }} />
                                    )}
                                    {m.fileType === 'audio' && (
                                      <audio src={m.remoteUrl} controls style={{ width: 200 }} />
                                    )}
                                    <IconButton
                                      size="large"
                                      onClick={() => handleRemoveMedia(q.id, idx)}
                                      sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        color: 'red'
                                      }}
                                    >
                                      <DeleteIcon fontSize="large" />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        )

                      case 'singleChoice':
                        if (q.allowOther && typeof val === 'object') {
                          return (
                            <Box>
                              {q.choices?.map((choice, idx) => (
                                <FormControlLabel
                                  key={idx}
                                  control={
                                    <Radio
                                      checked={val?.choice === choice}
                                      onChange={() => handleSingleChoiceChange(q.id, choice)}
                                      disabled={isSubmitting}
                                    />
                                  }
                                  label={choice}
                                />
                              ))}
                              <Box sx={{ mt: 1, pl: 3 }}>
                                <FormControlLabel
                                  control={
                                    <Radio
                                      checked={val?.otherChecked === true}
                                      onChange={() =>
                                        setAnswersState((prev) => ({
                                          ...prev,
                                          [q.id]: {
                                            ...prev[q.id],
                                            choice: '',
                                            otherChecked: !val.otherChecked
                                          }
                                        }))
                                      }
                                      disabled={isSubmitting}
                                    />
                                  }
                                  label={t('userAnswersPage.other')}
                                />
                                {val?.otherChecked && (
                                  <TextField
                                    size="small"
                                    placeholder={t('userAnswersPage.otherDesc')}
                                    value={val?.otherText || ''}
                                    onChange={(e) => handleOtherTextChange(q.id, e.target.value)}
                                    disabled={isSubmitting}
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          )
                        } else {
                          return (
                            <Box>
                              {q.choices?.map((choice, idx) => (
                                <FormControlLabel
                                  key={idx}
                                  control={
                                    <Radio
                                      checked={val === choice}
                                      onChange={() => handleSingleChoiceChange(q.id, choice)}
                                      disabled={isSubmitting}
                                    />
                                  }
                                  label={choice}
                                />
                              ))}
                            </Box>
                          )
                        }

                      case 'multipleChoice':
                        if (q.allowOther && typeof val === 'object') {
                          const sel = val?.choices || []
                          const otherChk = val?.otherChecked
                          return (
                            <Box>
                              {q.choices?.map((choice, idx) => {
                                const checked = sel.includes(choice)
                                return (
                                  <FormControlLabel
                                    key={idx}
                                    control={
                                      <Checkbox
                                        checked={checked}
                                        onChange={() => handleMultipleChoiceChange(q.id, choice)}
                                        disabled={isSubmitting}
                                      />
                                    }
                                    label={choice}
                                  />
                                )
                              })}
                              <Box sx={{ mt: 1, pl: 3 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={otherChk === true}
                                      onChange={() => toggleOtherChecked(q.id)}
                                      disabled={isSubmitting}
                                    />
                                  }
                                  label={t('userAnswersPage.other')}
                                />
                                {otherChk && (
                                  <TextField
                                    size="small"
                                    placeholder={t('userAnswersPage.otherDesc')}
                                    value={val?.otherText || ''}
                                    onChange={(e) => handleOtherTextChange(q.id, e.target.value)}
                                    disabled={isSubmitting}
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          )
                        } else {
                          const arr = val
                          return (
                            <Box>
                              {q.choices?.map((choice, idx) => (
                                <FormControlLabel
                                  key={idx}
                                  control={
                                    <Checkbox
                                      checked={arr.includes(choice)}
                                      onChange={() => handleMultipleChoiceChange(q.id, choice)}
                                      disabled={isSubmitting}
                                    />
                                  }
                                  label={choice}
                                />
                              ))}
                            </Box>
                          )
                        }

                      case 'ratingQuestion': {
                        const ratingButtons = []
                        for (let i = q.ratingMin; i <= q.ratingMax; i++) {
                          ratingButtons.push(i)
                        }
                        return (
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {q.ratingName} (Min: {q.ratingMin}, Max: {q.ratingMax})
                            </Typography>
                            {ratingButtons.map((num) => (
                              <FormControlLabel
                                key={num}
                                control={
                                  <Radio
                                    checked={val === num}
                                    onChange={() => handleRatingChange(q.id, num)}
                                    disabled={isSubmitting}
                                  />
                                }
                                label={String(num)}
                              />
                            ))}
                          </Box>
                        )
                      }

                      case 'videoQuestion':
                        return (
                          <Box>
                            {val?.videoUrl ? (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  {t('userAnswersPage.preview')}
                                </Typography>
                                <video src={val.videoUrl} controls style={{ width: '100%', height: 'auto' }} />
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {t('userAnswersPage.videoNoPreview')}
                              </Typography>
                            )}
                            {!val?.videoUrl && (
                              <Button
                                variant="outlined"
                                onClick={() => handleVideoQuestion(q.id)}
                                disabled={isSubmitting}
                              >
                                {t('userAnswersPage.startVideoRecord')}
                              </Button>
                            )}
                          </Box>
                        )

                      case 'photoCollage': {
                        const collageUrl = val?.collageSingleUrl
                        return (
                          <Box>
                            {collageUrl ? (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  {t('userAnswersPage.preview')}
                                </Typography>
                                <img
                                  src={collageUrl}
                                  alt="collage-result"
                                  style={{ maxWidth: '100%', border: '1px solid #ccc' }}
                                />
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {t('userAnswersPage.collageNoPhoto')}
                              </Typography>
                            )}
                            <Button
                              variant="outlined"
                              onClick={() => openCollageEditor(q.id)}
                              disabled={isSubmitting}
                              sx={{ mt: 2 }}
                            >
                              {t('userAnswersPage.collageEditor')}
                            </Button>
                          </Box>
                        )
                      }

                      case 'aiModerator':
                        return (
                          <AiModeratorChat
                            question={q}
                            conversation={val?.conversation || []}
                            onSendMessage={(userText) => handleAiModeratorSend(q.id, userText)}
                            isSubmitting={isSubmitting}
                            isAiLoading={aiLoading}
                          />
                        )

                      case 'infoQuestion':
                        return (
                          <Typography variant="body1" color="textSecondary">
                            {t('userAnswersPage.infoQuestionNoAnswer')}
                          </Typography>
                        )
                      
                      case 'cardSorting':
                        return (
                          <CardSortingComponent
                            question={q}
                            answersState={answersState}
                            setAnswersState={setAnswersState}
                          />
                        );

                      default:
                        return null
                    }
                  })()}
                </Paper>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                disabled={activeStep === 0 || isSubmitting}
                onClick={handleBack}
                variant="outlined"
              >
                {t('userAnswersPage.back')}
              </Button>
              {isLastStep ? (
                <Button variant="contained" onClick={handleSubmitAnswers} disabled={isSubmitting}>
                  {t('userAnswersPage.send')}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext} disabled={isSubmitting}>
                  {t('userAnswersPage.next')}
                </Button>
              )}
            </Box>
          </Paper>
        )}

        {/* Kolaj Dialog */}
        <Dialog
          open={collageDialogOpen}
          onClose={handleCancelCollage}
          fullWidth
          maxWidth="sm"
          disableEnforceFocus
        >
          <DialogTitle>{t('userAnswersPage.collageEditorTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('userAnswersPage.collageEditorIntro')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCollageImage}
              sx={{ mb: 2 }}
            >
              {t('userAnswersPage.collageAddPhoto')}
            </Button>

            <CollageCanvas
              ref={collageRef}
              collageItems={collageItems}
              setCollageItems={setCollageItems}
            />

            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              {t('userAnswersPage.addedPhotos')}
            </Typography>
            {collageItems.map((item, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <img
                  src={item.imageUrl}
                  alt="collage"
                  style={{ width: 40, height: 40, objectFit: 'cover' }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleRemoveCollageItem(idx)}
                >
                  {t('userAnswersPage.collageRemove')}
                </Button>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelCollage} color="error">
              {t('userAnswersPage.collageEditorCancel')}
            </Button>
            <Button onClick={handleSaveCollage} variant="contained">
              {t('userAnswersPage.collageEditorSave')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Video Recording Dialog */}
        <Dialog open={videoDialogOpen} onClose={handleCancelVideo} fullWidth maxWidth="sm">
          <DialogTitle>{t('userAnswersPage.videoRecordTitle')}</DialogTitle>
          <DialogContent>
            {isRecording && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'red' }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: 'red',
                    animation: 'blinker 1s linear infinite'
                  }}
                />
                <Typography variant="body2" color="red">
                  {t('userAnswersPage.videoRecording')}
                </Typography>
              </Box>
            )}
            {!videoPreviewUrl ? (
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  backgroundColor: '#000'
                }}
                playsInline
              />
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('userAnswersPage.videoRecordedMsg')}
                </Typography>
                <video src={videoPreviewUrl} controls style={{ width: '100%' }} />
              </Box>
            )}
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!videoPreviewUrl && (
                <Button variant="contained" onClick={handleToggleRecording}>
                  {isRecording
                    ? t('userAnswersPage.videoRecordStop')
                    : t('userAnswersPage.videoRecordStart')}
                </Button>
              )}
              {videoPreviewUrl && (
                <Typography variant="body2" color="textSecondary">
                  {t('userAnswersPage.videoRecordedMsg')}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelVideo} color="error">
              {t('userAnswersPage.videoDialogCancel')}
            </Button>
            <Button onClick={handleConfirmVideo} variant="contained" disabled={!videoPreviewUrl}>
              {t('userAnswersPage.videoDialogConfirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  )
}